// IMPORTS

// external
import algosdk, { Algodv2, Transaction, assignGroupID } from "algosdk"
import request from 'superagent';

// global
import { Network, getAnalyticsEndpoint, getNetworkName } from "../../globals"
import { storeTransferDetails } from "../../stateUtils"
import AlgofiClient from "../../algofiClient" 
import AlgofiUser from "../../algofiUser"
import ParsedTransaction from "../../parsedTransaction"
import { Base64Encoder } from "../../encoder"

// interface
import { LENDING_POOL_INTERFACE_STRINGS } from "../../interfaces/lendingPoolInterfaceConfig"

// local
import { PoolType, POOL_STRINGS } from "./ammConfig"
import ManagerConfig, { ManagerConfigs } from "./managerConfig"
import PoolConfig from "./poolConfig"
import Pool from "./pool"
import LinearPool from "./linearPool"

// INTERFACE

export default class AMMClient {
  public algofiClient: AlgofiClient
  public algod: Algodv2
  public network: Network
  public managerAppId: number
  public pools: { [key: number]: Pool } = {} // appId -> pool
  public assetPoolMap : { [key: number]: Pool[] } = {} // asset -> pools
  public poolMap: { [key: number] : { [key: number] : { [key: number] : Pool } } } = {} // asset1, asset2, type -> pool
  public lpPoolMap: { [key: number]: Pool } = {} // lp asset id -> pool

  public linearPool: LinearPool

  constructor(algofiClient: AlgofiClient) {
    this.algofiClient = algofiClient
    this.algod = this.algofiClient.algod
    this.network = this.algofiClient.network
    this.managerAppId = ManagerConfigs[this.network].appId
    // special linear pool for usdc/stbl
    this.linearPool = new LinearPool(this.algod)
  }

  async loadState() {
    await this.linearPool.loadState()
    
    await request
      .get(getAnalyticsEndpoint(this.network) + "/pools?network=" + getNetworkName(this.network))
      .then(resp => {
        if (resp.status == 200) {
          for (const poolInfo of resp.body) {
            if (!(poolInfo.appId in this.pools)) {
              let config = new PoolConfig(poolInfo.app_id, poolInfo.asset1_id, poolInfo.asset2_id, poolInfo.lp_asset_id, PoolType[poolInfo.type as keyof typeof PoolType])
              // pools
              this.pools[config.appId] = new Pool(this.algod, this, config, poolInfo.tvl, poolInfo.apr_data.week)
              // assetPoolMap
              if (!(config.asset1Id in this.assetPoolMap)) {
                this.assetPoolMap[config.asset1Id] = []
              }
              if (!(config.asset2Id in this.assetPoolMap)) {
                this.assetPoolMap[config.asset2Id] = []
              }
              this.assetPoolMap[config.asset1Id].push(this.pools[config.appId])
              this.assetPoolMap[config.asset2Id].push(this.pools[config.appId])
              // poolMap
              if (!(config.asset1Id in this.poolMap)) {
                this.poolMap[config.asset1Id] = {}
              }
              if (!(config.asset2Id in this.poolMap[config.asset1Id])) {
                this.poolMap[config.asset1Id][config.asset2Id] = {}
              }
              this.poolMap[config.asset1Id][config.asset2Id][config.poolType] = this.pools[config.appId]
              // lpPoolMap
              this.lpPoolMap[config.lpAssetId] = this.pools[config.appId]
            }
           }
         } else {
           console.log("Bad Response")
         }
      })
      .catch(err => {
        console.log(err.message)
      });
  }
  
  async getPool(assetAId: number, assetBId: number, poolType: PoolType): Promise<Pool> {
    if (assetAId == assetBId) {
      throw new Error("Asset IDs must differ")
    }
    
    // normalize asset order
    let asset1Id = (assetAId < assetBId) ? assetAId : assetBId
    let asset2Id = (assetAId < assetBId) ? assetBId : assetAId
    
    if (this.poolMap?.[asset1Id]?.[asset2Id]?.[poolType]) {
      await this.poolMap[asset1Id][asset2Id][poolType].loadState()
      return this.poolMap[asset1Id][asset2Id][poolType]
    }
    
    if (poolType == PoolType.NANO || poolType == PoolType.MOVING_RATIO_NANO) {
      throw new Error("pool not found")
    }

    let pool = new Pool(this.algod, this, new PoolConfig(0, asset1Id, asset2Id, 0, poolType))
    await pool.loadState()
    return pool
  }
  
  hasPoolForLPAsset(lpAssetId: number): boolean {
    return (lpAssetId in this.lpPoolMap)
  }
  
  async getPoolByLPAsset(lpAssetId: number): Promise<Pool> {
    if (!(lpAssetId in this.lpPoolMap)) {
      throw new Error("Pool not found")
    }

    let pool = this.lpPoolMap[lpAssetId]
    await pool.loadState()
    return pool
  }
  
  hasPoolsForAsset(assetId: number): boolean {
    return (assetId in this.assetPoolMap)
  }
  
  getPoolsByAsset(assetId: number): Pool[] {
    if (!(assetId in this.assetPoolMap)) {
      return []
    }

    return this.assetPoolMap[assetId]
  }
  
  hasPoolForAppId(appId: number): boolean {
    return (appId in this.pools)
  }
  
  async getPoolByAppId(appId: number): Promise<Pool> {
    if (!(appId in this.pools)) {
      throw new Error("Pool not found")
    }

    let pool =  this.pools[appId]
    await pool.loadState()
    return pool
  }

  isAMMTransaction(txn : object) : boolean {
    let appId = txn['application-transaction']['application-id']
    return (appId in this.pools || appId == this.algofiClient.interfaces.lendingPools)
  }
  
  parseTransaction(txns: {}[], txnIdx: number, parsedTransactions: ParsedTransaction[]) {
    const twoPreviousTxn = txns[txnIdx + 2]
    const previousTxn = txns[txnIdx + 1]
    const txn = txns[txnIdx]
    const subsequentTxn = txns[txnIdx - 1]
    const twoSubsequentTxn = txns[txnIdx - 2]
    let appId = txn['application-transaction']['application-id']

    let assetsIn = {}
    let assetsOut = {}

    let command = Base64Encoder.decode(txn['application-transaction']['application-args'][0])

    if (appId in this.pools) {
      switch(command) {
        case POOL_STRINGS.pool:
          {
            storeTransferDetails(twoPreviousTxn, assetsIn)
            storeTransferDetails(previousTxn, assetsIn)
            storeTransferDetails(txn['inner-txns'].at(-1), assetsOut)
            if (subsequentTxn['inner-txns']) {
              storeTransferDetails(subsequentTxn['inner-txns'].at(-1), assetsOut)
            }
            if (twoSubsequentTxn['inner-txns']) {
              storeTransferDetails(twoSubsequentTxn['inner-txns'].at(-1), assetsOut)
            }

            parsedTransactions.push(
              new ParsedTransaction(txn, "AMM", appId, "POOL", [], assetsIn, assetsOut)
            )
            
            break
          }
        case POOL_STRINGS.swap_exact_for:
          {
            storeTransferDetails(previousTxn, assetsIn)
            storeTransferDetails(txn['inner-txns'].at(-1), assetsOut)
            
            parsedTransactions.push(
              new ParsedTransaction(txn, "AMM", appId, "SWAP", [], assetsIn, assetsOut)
            )
            
            break
          }
        case POOL_STRINGS.swap_for_exact:
          {
            storeTransferDetails(previousTxn, assetsIn)
            storeTransferDetails(txn['inner-txns'].at(-1), assetsOut)
            if (subsequentTxn['inner-txns']) {
              storeTransferDetails(subsequentTxn['inner-txns'].at(-1), assetsOut)
            }
            
            parsedTransactions.push(
              new ParsedTransaction(txn, "AMM", appId, "SWAP", [], assetsIn, assetsOut)
            )
            
            break
          }
        case POOL_STRINGS.burn_asset1_out:
          {
            storeTransferDetails(previousTxn, assetsIn)
            storeTransferDetails(txn['inner-txns'].at(-1), assetsOut)
            storeTransferDetails(subsequentTxn['inner-txns'].at(-1), assetsOut)
            
            parsedTransactions.push(
              new ParsedTransaction(txn, "AMM", appId, "BURN", [], assetsIn, assetsOut)
            )
            
            break
          }
        default:
          return
      }
    } else if (appId in this.algofiClient.interfaces.lendingPools) {
        return
    }
  }
  
}
